<template>
  <div v-if="!isLoading" class="create-adventure overflow-hidden pb-72">
    <ProgressBar v-if="!isEdit" :value="currentStep || 1" :max="allCreateRoutes.length" content="Adventure Progress Bar" />
    <section class="flex flex-col items-center mb-48">
      <AlertBanner v-if="alert.message" :status="alert.status" class="w-full sticky z-5 mb-16">
        <span>{{ alert.message }}</span>
      </AlertBanner>
      <router-view v-slot="{ Component }">
        <transition :name="animationName" mode="out-in">
          <component
            :is="Component"
            v-if="Component"
            :key="$route.fullPath"
            class="transition-all duration-150"
            :all-categories="allCategories"
            :adventure-meta-data="adventureMetaData"
            :sitter-profile="sitterProfile"
            :is-edit="isEdit"
            @alert="alertHandler"
          >
            <Breadcrumb
              v-if="(currentStep !== 1 || isEdit) && previousRoute"
              :header="isEdit ? 'Cancel' : previousRoute.name"
              :to="isEdit ? `/adventures/${adventureId}/edit` : `/adventures/new/${previousRoute.path}`"
              :router-link="true"
            />
          </component>
        </transition>
      </router-view>
    </section>
  </div>
</template>

<script lang="ts">
import pickBy from 'lodash/pickBy';
import ProgressBar from '../../../components/progress_bar/ProgressBar.vue';
import AlertBanner from '../../../components/alert_banner/AlertBanner.vue';
import Breadcrumb from '../../../components/breadcrumb/Breadcrumb.vue';

import { ADVENTURE_METADATA_QUERY, SITTER_PROFILE_QUERY, SITTER_ADVENTURES_QUERY } from '../../../graphql/adventures/queries';
import { UPDATE_DRAFT_ADVENTURE, CREATE_DRAFT_ADVENTURE } from '../../../graphql/adventures/mutations';

export default {
  name: 'Create',
  components: { ProgressBar, AlertBanner, Breadcrumb },
  data() {
    return {
      animationName: '',
      alert: {},
      isLoading: true,
    };
  },
  computed: {
    allCreateRoutes(): any {
      const routes = this.$router?.options?.routes
        ?.find((route) => route.path === '/adventures')
        ?.children?.find((child) => child.path === 'new')?.children;
      if (routes === undefined) {
        throw new TypeError('Route undefined');
      }
      return routes;
    },
    currentStep(): number {
      return this.$route?.meta?.step || this.$route?.matched[2]?.meta?.step || this.$route?.matched[1]?.meta?.step;
    },
    adventureId(): string {
      return this.$store.getters['adventures/adventureId'];
    },
    previousRoute(): { name: string; path: string } {
      const targetStep = this.currentStep - 1;
      const foundRoute = this.allCreateRoutes?.find((route) => route.meta.step === targetStep);

      // If the route has children, find the appropriate child route to navigate to
      if (foundRoute?.children && foundRoute.children.length > 0) {
        // For parent routes with children, find the first child with trackCreateFlow or the first child
        const childRoute = foundRoute.children.find((child) => child.meta?.trackCreateFlow) || foundRoute.children[0];
        return {
          name: childRoute.name,
          path: foundRoute.path + (childRoute.path ? `/${childRoute.path}` : ''),
        };
      }

      return foundRoute;
    },
    isEdit(): boolean {
      return this.$route.matched?.some((record) => record.meta?.mode === 'edit') ?? false;
    },
  },
  watch: {
    $route(to, from) {
      this.alert = {};
      const toDepth = to?.matched[1].meta?.step;
      const fromDepth = from?.matched[1].meta?.step;
      this.animationName = toDepth >= fromDepth || toDepth === undefined ? 'slide-left' : 'slide-right';

      if (!this.isEdit) {
        this.createUpdateDraft();
      }
    },
  },
  created() {
    if (this.isEdit) {
      this.isLoading = false;
    }
  },
  methods: {
    alertHandler(status, message) {
      this.alert = {
        status,
        message,
      };
    },
    createUpdateDraft() {
      if (this.$store.getters['adventures/adventureDraft'].id) {
        this.$apollo
          .mutate({
            mutation: UPDATE_DRAFT_ADVENTURE,
            variables: {
              id: this.$store.getters['adventures/adventureDraft'].id,
              input: this.$store.getters['adventures/apolloMutationInput'],
            },
          })
          .catch((e) => {
            console.error(e);
          });
      } else {
        this.$apollo
          .mutate({
            mutation: CREATE_DRAFT_ADVENTURE,
            variables: {
              input: this.$store.getters['adventures/apolloMutationInput'],
            },
          })
          .then(({ data }) => {
            this.$store.dispatch('adventures/updateStore', { id: data.createDraftAdventure.adventure.id });
          })
          .catch((e) => {
            console.error(e);
          });
      }
    },
  },

  apollo: {
    allCategories: {
      query: ADVENTURE_METADATA_QUERY,
    },
    adventureMetaData: {
      query: ADVENTURE_METADATA_QUERY,
      variables() {
        return {
          id: this.$store.getters['adventures/adventureDraft'].adventureCategoryId,
        };
      },
    },
    sitterProfile: {
      query: SITTER_PROFILE_QUERY,
    },
    sitterAdventures: {
      query: SITTER_ADVENTURES_QUERY,
      skip() {
        return this.isEdit;
      },
      variables: {
        status: ['draft'],
      },
      fetchPolicy: 'network-only',
      async result({ data }) {
        if (data.sitterAdventures.edges.length && !this.$store.getters['adventures/adventureDraft'].id) {
          const adventureData = data.sitterAdventures?.edges[0]?.node?.adventure;
          const sitterAvailability = pickBy(adventureData?.sitterAvailability, (value) => Array.isArray(value));
          const ageRangeIds = adventureData.ageRanges.map((ageRange) => ageRange.id);
          const duration = adventureData.duration < 60 ? null : adventureData.duration;

          const adventure = {
            ...adventureData,
            sitterAvailability,
            ageRangeIds,
            duration,
          };

          await this.$store.dispatch('adventures/updateStore', adventure);
        }

        if (!this.isEdit && this.$route.path !== this.$store.getters['adventures/resumePath']) {
          await this.$router.replace({ path: this.$store.getters['adventures/resumePath'] });
        }
        this.isLoading = false;
      },
    },
  },
};
</script>

<style lang="scss">
.slide-left-enter,
.slide-right-leave-active {
  opacity: 0;
  margin-left: 250px;
}
.slide-right-enter,
.slide-left-leave-active {
  opacity: 0;
  margin-left: -250px;
}
</style>
